import {
    EXCEL_ADDIN_AUTH_FLAG,
    EXCEL_ADDIN_AUTH_PARAM,
    EXCEL_ADDIN_LOGOUT_FLAG,
    EXCEL_ADDIN_LOGOUT_PARAM,
    EXCEL_ADDIN_LOGOUT_STARTED_FLAG,
} from './constants';
import { ExcelAddinDialogMode, OfficeGlobal } from './types';

/** Session storage can throw in restrictive webviews like sign-in dialog */
const safeSessionStorage = {
    get: (key: string): string | null => {
        try {
            return window.sessionStorage.getItem(key);
        } catch {
            return null;
        }
    },
    set: (key: string, value: string): void => {
        try {
            window.sessionStorage.setItem(key, value);
        } catch {
            // Ignore: the query parameter still covers the first leg of the flow.
        }
    },
    remove: (key: string): void => {
        try {
            window.sessionStorage.removeItem(key);
        } catch {
            // Ignore.
        }
    },
};

const DIALOG_MARKERS: { mode: ExcelAddinDialogMode; param: string; flag: string }[] = [
    { mode: 'signIn', param: EXCEL_ADDIN_AUTH_PARAM, flag: EXCEL_ADDIN_AUTH_FLAG },
    { mode: 'signOut', param: EXCEL_ADDIN_LOGOUT_PARAM, flag: EXCEL_ADDIN_LOGOUT_FLAG },
];

/**
 * Which Excel add-in dialog, if any, is this page load?
 *
 * Recognises both legs of the flow: the first, where the pane opens `/?excelAddinAuth=1`, and the
 * return, where Auth0 has replaced the query string with its own parameters. Reading also records the
 * flag, so call this early - before Auth0 processes the callback.
 */
export const getExcelAddinDialogMode = (): ExcelAddinDialogMode | null => {
    const params = new URLSearchParams(window.location.search);

    const fromParam = DIALOG_MARKERS.find(marker => params.has(marker.param));
    if (fromParam) {
        safeSessionStorage.set(fromParam.flag, '1');
        return fromParam.mode;
    }

    return DIALOG_MARKERS.find(marker => safeSessionStorage.get(marker.flag) === '1')?.mode ?? null;
};

export const clearDialogFlags = (): void => {
    DIALOG_MARKERS.forEach(marker => safeSessionStorage.remove(marker.flag));
    safeSessionStorage.remove(EXCEL_ADDIN_LOGOUT_STARTED_FLAG);
};

export const hasLogoutStarted = (): boolean =>
    safeSessionStorage.get(EXCEL_ADDIN_LOGOUT_STARTED_FLAG) === '1';

export const markLogoutStarted = (): void =>
    safeSessionStorage.set(EXCEL_ADDIN_LOGOUT_STARTED_FLAG, '1');

let officeJsLoad: Promise<OfficeGlobal> | null = null;

const loadOfficeJs = (officeJsUrl: string): Promise<OfficeGlobal> => {
    const alreadyLoaded = window.Office;
    if (alreadyLoaded) {
        return Promise.resolve(alreadyLoaded);
    }

    if (officeJsLoad) {
        return officeJsLoad;
    }

    officeJsLoad = new Promise<OfficeGlobal>((resolve, reject) => {
        const settle = () => {
            const office = window.Office;
            if (office) {
                resolve(office);
            } else {
                officeJsLoad = null;
                reject(new Error('office.js loaded but window.Office is missing.'));
            }
        };
        const fail = () => {
            officeJsLoad = null;
            reject(new Error('Could not load office.js. Check the network connection.'));
        };

        // Another copy may already be on the page - from Office itself, or a previous render.
        const existing = document.querySelector<HTMLScriptElement>(`script[src="${officeJsUrl}"]`);
        if (existing) {
            existing.addEventListener('load', settle);
            existing.addEventListener('error', fail);
            return;
        }

        const script = document.createElement('script');
        script.src = officeJsUrl;
        script.onload = settle;
        script.onerror = fail;
        document.head.appendChild(script);
    });

    return officeJsLoad;
};

/**
 * Send a result to the task pane that opened this dialog.
 *
 * `messageParent` only passes strings, and only works from a page on the same origin as the pane -
 * which is why this lives in the app rather than in the add-in bundle.
 */
export const sendResultToTaskPane = async (result: object, officeJsUrl: string): Promise<void> => {
    const office = await loadOfficeJs(officeJsUrl);
    await office.onReady();
    office.context.ui.messageParent(JSON.stringify(result));
};
